import React, { memo, useCallback } from "react";

import { useMutation } from "@apollo/client";
import {
  Button,
  Center,
  FormControl,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Select,
  Text,
  VStack,
} from "@chakra-ui/react";
import {
  EditMessageThreadLocaleMutation,
  EditMessageThreadLocaleMutationVariables,
} from "gql/graphql";
import MESSAGE_LIST_QUERY from "modules/messaging/_networking/message-list";
import { useAuth } from "modules/providers/auth-provider";
import { useMixpanel } from "modules/providers/mixpanel-provider";
import { MIXPANEL_EVENT_TYPE } from "modules/providers/mixpanel-provider/mixpanel-provider";
import useUserPreference from "modules/hooks/use-user-preference";
import { FieldValues, useForm } from "react-hook-form";
import colors from "app/colors";
import { supportedTranslationLanguages } from "modules/utils/translation";
import EDIT_MESSAGE_THREAD_LOCALE_MUTATION from "./networking/edit-message-thread-locale";

import { getMessageThreadTypeName } from "modules/utils/Functions";
import { useOverlay } from "modules/providers/overlay-provider";

interface IProps {
  messageThreadId?: string;
}

const EditUserLocaleModal = ({ messageThreadId }: IProps) => {
  const { me } = useAuth();
  const { stringValue: currentLocale, upsertUserPreference } =
    useUserPreference({
      userId: me?.id,
      name: "LOCALE",
    });
  const { track } = useMixpanel();
  const { closeModal } = useOverlay();

  const [editMessageThreadLocaleAPI] = useMutation<
    EditMessageThreadLocaleMutation,
    EditMessageThreadLocaleMutationVariables
  >(EDIT_MESSAGE_THREAD_LOCALE_MUTATION, {
    refetchQueries: [
      {
        query: MESSAGE_LIST_QUERY,
        variables: { messageThreadId },
      },
    ],
  });

  const {
    handleSubmit,
    formState: { errors },
    register,
  } = useForm({
    mode: "onSubmit",
  });

  const onSubmit = useCallback(
    async ({ locale }: FieldValues) => {
      if (!me) return;

      await upsertUserPreference(locale);
      track(MIXPANEL_EVENT_TYPE.USER_PREFERRED_LANGUAGE_SET, {
        "Preferred Language Set": locale,
      });

      if (messageThreadId) {
        const response = await editMessageThreadLocaleAPI({
          variables: {
            id: messageThreadId,
            locale,
          },
        });
        if (response.data?.editMessageThread) {
          track(MIXPANEL_EVENT_TYPE.CONVERSATION_TRANSLATION_TOGGLED, {
            Translated: !!locale,
            "Target Language": locale,
            "Source Language": "various",
            "Conversation Type": getMessageThreadTypeName(
              response.data.editMessageThread,
            ),
          });
        }
      }

      closeModal();
    },
    [
      me,
      upsertUserPreference,
      track,
      messageThreadId,
      closeModal,
      editMessageThreadLocaleAPI,
    ],
  );

  return (
    <Modal isOpen onClose={closeModal} size="xs" isCentered>
      <ModalOverlay />
      <ModalContent borderRadius={16}>
        <ModalHeader>
          <Center>
            <VStack>
              <Text>
                {messageThreadId
                  ? `Translate Conversation`
                  : `Preferred Langauge`}
              </Text>
              {messageThreadId && (
                <Text fontSize={14} fontWeight={500} color={colors.gray50}>
                  Select preferred language
                </Text>
              )}
            </VStack>
          </Center>
          <ModalCloseButton color={colors.grayMain} />
        </ModalHeader>
        <ModalBody>
          <form id="edit-translate-form" onSubmit={handleSubmit(onSubmit)}>
            <FormControl isInvalid={!!errors?.locale}>
              <VStack>
                <Select
                  placeholder="Select Language"
                  fontSize={14}
                  {...register("locale")}
                  defaultValue={currentLocale ?? undefined}
                >
                  {supportedTranslationLanguages.map(([langCode, langName]) => (
                    <option key={langCode} value={langCode}>
                      {langName}
                    </option>
                  ))}
                </Select>
                <HStack>
                  <Button
                    variant="cancel"
                    height={8}
                    w={28}
                    onClick={(e) => {
                      e.stopPropagation();
                      closeModal();
                    }}
                  >
                    Cancel
                  </Button>
                  <Button height={8} w={28} type="submit">
                    Save
                  </Button>
                </HStack>
              </VStack>
            </FormControl>
          </form>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default memo(EditUserLocaleModal);
